import React, { forwardRef } from 'react';
import clsx from 'clsx';
import config from 'appconfig'; // eslint-disable-line import/no-unresolved
import { uiAdjustedUserObject } from 'relevant-shared/misc/userUtils';
import { stores } from '../stores';
import { useUserById } from '../stores/userQuery';

type AvatarProps = {
	text?: string;
	img?: string;
	className?: string;
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(({ text, img, className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={clsx(
				'relative flex aspect-square min-w-6 min-h-6 items-center justify-center overflow-clip rounded-full bg-grey-150',
				'shadow-[0_0_0_1px_var(--color-white)]', // Using drop shadow to create a border since the rounded 1px white border rendered awfully on Firefox
				className,
			)}
			style={{
				backgroundColor: text ? getAvatarColor(text) : undefined,
				containerType: 'size',
			}}
			{...props}
		>
			{text && (
				<svg
					width="100%"
					height="100%"
					viewBox="0 0 100 100"
					className="absolute inset-0 select-none"
				>
					<text
						x="50"
						y="50"
						dominantBaseline="central"
						textAnchor="middle"
						fill="white"
						fontWeight="650"
						fontSize={text.length > 2 ? '40' : '50'}
						className="capitalize"
						style={{
							textRendering: 'geometricPrecision',
						}}
					>
						{text}
					</text>
				</svg>
			)}
			{/* Overlay image over initials */}
			{img && (
				<div className="absolute inset-0 flex items-center justify-center bg-transparent">
					<img src={img} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
				</div>
			)}
		</div>
	);
});

function getAvatarColor(name: string) {
	const materialColors = [
		'#F44336', // Red
		'#673AB7', // Deep Purple
		'#3F51B5', // Indigo
		'#2196F3', // Blue
		'#009688', // Teal
		'#4CAF50', // Green
		'#FF9800', // Orange
		'#795548', // Brown
		'#607D8B', // Blue Grey
	];

	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}

	return materialColors[Math.abs(hash) % materialColors.length];
}

type UserAvatarProps = {
	userId: string;
	fallbackUser?: Record<string, unknown>;
} & AvatarProps;

export const UserAvatar = forwardRef<HTMLDivElement, UserAvatarProps>(({ userId, fallbackUser, ...props }, ref) => {
	const user = uiAdjustedUserObject(useUserById(userId) || fallbackUser, stores.identity.isSuperAdministrator());

	return (
		<Avatar
			ref={ref}
			text={user?.initials}
			key={stores.users.timesSet.toString() /** image might have changed, even when URL hasn't */}
			img={user?.picture && `${user.picture.noModify ? '' : config.apis.relevant.replace(/\/$/, '')}${user.picture}`}
			{...props}
		/>
	);
});

export const CurrentUserAvatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
	const user = stores.identity.user();

	return (
		<UserAvatar
			userId={user.id}
			{...props}
		/>
	);
});
